import React, { useEffect, useState, useRef } from 'react';
import ServiceBanner from '../../components/ServiceBanner';
import ServicePlan from '../../components/ServicePlan';
import { getSeoByUrl } from '../../api/api';
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import ServicePremium from '../../components/ServicePremimum';

const Service = () => {

  const [seoData, setSeoData] = useState({});
  const [premiumLoaded, setPremiumLoaded] = useState(false);
  const location = useLocation();
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const response = await getSeoByUrl("service");
        if (response.success && response.data) {
          setSeoData(response.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchSeoData();
  }, []);

  useEffect(() => {
    if (!premiumLoaded) return;

    const hash = location.hash;
    if (!hash) return;

    const id = hash.replace("#", "");

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    const scrollToElement = () => {
      let element = document.getElementById(id);

      if (!element) {
        const idMap = {
          'starter': ['starter', 'starter-program'],
          'signature': ['signature', 'signature-program-with-genetic-testing'],
          'signature-plus': ['signature-plus', 'signature-program-with-genetic-&-gut-testing']
        };

        const alternatives = idMap[id] || [];
        for (const altId of alternatives) {
          if (altId !== id) {
            const altElement = document.getElementById(altId);
            if (altElement) {
              element = altElement;
              break;
            }
          }
        }
      }

      if (element) {
        const offset = 120;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        return true;
      }
      return false;
    };

    let found = scrollToElement();

    if (!found) {
      let attempts = 0;
      const maxAttempts = 15;

      const retryScroll = () => {
        attempts++;
        const success = scrollToElement();

        if (success || attempts >= maxAttempts) {
          if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
          }
          return;
        }
        scrollTimeoutRef.current = setTimeout(retryScroll, 500);
      };

      scrollTimeoutRef.current = setTimeout(retryScroll, 300);
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, [premiumLoaded, location.hash]);

  return (
    <div>
      <Helmet>
        {/* ================= PRIMARY META ================= */}
        <title>{seoData.metaTitle || "Services | Nutrillion"}</title>

        <meta
          name="description"
          content={seoData.metaDescription || "Explore Nutrillion's personalized nutrition services and DNA-based diet plans."}
        />

        {seoData.metaKeywords && (
          <meta name="keywords" content={seoData.metaKeywords} />
        )}

        <meta
          name="robots"
          content={seoData.robotsTxt || "index, follow"}
        />

        <link
          rel="canonical"
          href={seoData.canonicalUrl || "https://nutrillion.one/service"}
        />

        {/* ================= OPEN GRAPH ================= */}
        <meta property="og:type" content={seoData.ogType || "website"} />
        <meta property="og:title" content={seoData.ogTitle || seoData.metaTitle || "Services | Nutrillion"} />
        <meta property="og:description" content={seoData.ogDescription || seoData.metaDescription || "Explore Nutrillion's personalized nutrition services and DNA-based diet plans."} />
        <meta property="og:url" content={seoData.ogUrl || seoData.canonicalUrl || "https://nutrillion.one/service"} />
        {seoData.ogImage && (
          <meta property="og:image" content={seoData.ogImage} />
        )}
        <meta property="og:site_name" content="Nutrillion" />

        {/* ================= TWITTER CARD ================= */}
        <meta name="twitter:card" content={seoData.twitterCard || "summary"} />
        <meta name="twitter:title" content={seoData.twitterTitle || seoData.metaTitle || "Services | Nutrillion"} />
        <meta name="twitter:description" content={seoData.twitterDescription || seoData.metaDescription || "Explore Nutrillion's personalized nutrition services and DNA-based diet plans."} />
        {(seoData.twitterImage || seoData.ogImage) && (
          <meta name="twitter:image" content={seoData.twitterImage || seoData.ogImage} />
        )}

      {seoData.schemaMarkup && (
          <script type="application/ld+json">
            {JSON.stringify(
              typeof seoData.schemaMarkup === "string"
                ? JSON.parse(seoData.schemaMarkup)
                : seoData.schemaMarkup
            )}
          </script>
        )}
      </Helmet>

      <ServiceBanner />

      <ServicePremium
        onLoaded={() => {
          setPremiumLoaded(true);
        }}
      />

      <ServicePlan />
    </div>
  );
}

export default Service;